// INC2-INTAKE in the in-memory API: Workforce › Intake (services/api/src/routes/intake.ts). The
// fake company has received nothing (no sample data): both lists are empty and a job id is 404,
// as on a real company before any connection or form sends a notice. Tests add rows with `add*`.
import type { OperationOutput, RouteId } from "@companyos/sdk";
import { type Handler, json, notFound, problem } from "./fake";

export type FakeInboundEvent = OperationOutput<"intake.events">["data"][number];
export type FakeIntegrationJob = OperationOutput<"intake.jobs">["data"][number];

export const FAKE_INTAKE_ROUTES = [
  "intake.events",
  "intake.jobs",
  "intake.retryJob",
  "intake.discardJob",
] as const satisfies readonly RouteId[];

export function createIntakeFake(deps: { now: () => Date }) {
  const events: FakeInboundEvent[] = [];
  const jobs: FakeIntegrationJob[] = [];
  const newest = <T extends { id: string }>(a: T, b: T) => (a.id < b.id ? 1 : -1);
  const job = (companyId: string, id: string) =>
    jobs.find((x) => x.company_id === companyId && x.id === id) ?? null;

  const handlers: Partial<Record<RouteId, Handler>> = {
    "intake.events": ({ principal, url }) => {
      if (!principal) return notFound();
      const status = url.searchParams.get("status");
      const data = events
        .filter((x) => x.company_id === principal.company.id && (!status || x.status === status))
        .sort(newest);
      return json({ data, next_cursor: null, prev_cursor: null });
    },
    "intake.jobs": ({ principal, url }) => {
      if (!principal) return notFound();
      const state = url.searchParams.get("state");
      const data = jobs
        .filter((x) => x.company_id === principal.company.id && (!state || x.state === state))
        .sort(newest);
      return json({ data, next_cursor: null, prev_cursor: null });
    },
    "intake.retryJob": ({ principal, params }) => {
      if (!principal) return notFound();
      const x = job(principal.company.id, params.id ?? "");
      if (!x) return notFound();
      if (x.state !== "dead")
        return problem(409, "integration_job_state_conflict", "Not possible in the job's state");
      Object.assign(x, {
        state: "queued",
        attempts: 0,
        dead_reason: null,
        next_at: deps.now().toISOString(),
        updated_at: deps.now().toISOString(),
      });
      return json(x);
    },
    "intake.discardJob": ({ principal, params }) => {
      if (!principal) return notFound();
      const x = job(principal.company.id, params.id ?? "");
      if (!x) return notFound();
      if (x.state !== "dead" && x.state !== "queued")
        return problem(409, "integration_job_state_conflict", "Not possible in the job's state");
      Object.assign(x, { state: "discarded", updated_at: deps.now().toISOString() });
      return json(x);
    },
  };

  return {
    handlers,
    /** Tests: something a company received. */
    addEvent: (e: FakeInboundEvent) => events.push(e),
    /** Tests: a job in a company's queue (a dead letter, say). */
    addJob: (j: FakeIntegrationJob) => jobs.push(j),
  };
}
